/** /tour-operators/:id/readiness — a sandbox's **readiness scorecard** (client MCP plan §13.5;
 *  WPS FE-05, backend P1b-08; `console_integration_readiness_view`, sandboxes only).
 *
 *  The integration playbook's five "done" criteria over the sandbox's recent activity (the last
 *  14 days by default, fewer from the Activity menu) — the same scorecard its developer gets from
 *  the MCP tool `check_readiness`. **Passing never unlocks production: a person does**, and the
 *  page says so.
 *
 *  - The verdict: ready or not, how many criteria pass, the window, what the activity was, and
 *    what could not be read (`partial`: no longer stored, too large, past the caps or the time
 *    budget) — the criteria judge what was read.
 *  - Each criterion, in the playbook's order: pass or fail and why (no activity yet / not shown
 *    yet / violated), its summary and counts, the next step and the playbook's Done section.
 *  - Its evidence quotes what the developer's system sent (booking ids, values; contacts masked,
 *    at most 80 characters): one `UntrustedBlock` per criterion, each fact an `UntrustedText`
 *    whose label carries only our parts (scenario, import, when); the rest is counted.
 *
 *  Read-only. A production operator has none (409 `not_sandbox`); a timed-out read offers the
 *  extended budget. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import * as readinessApi from '@/api/integrationReadiness';
import {
  DAY_OPTIONS,
  MAX_DAYS,
  REASON_LABELS,
  SCENARIO_LABELS,
  UNREAD_LABELS,
  countLabel,
  readinessKeys,
  type IntegrationReadiness,
  type ReadinessCriterion,
  type ReadinessEvidence,
} from '@/api/integrationReadiness';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { SectionCard } from '@/components/ReadView';
import { UntrustedBlock, UntrustedText } from '@/components/UntrustedText';
import { formatDate } from '@/utils/format';
import { importRunsHref } from '@/utils/gridLinks';

const EVIDENCE_LEGEND =
  'Quoted from what the sandbox’s developer sent — booking ids and values as received (contacts masked, values cut at 80 characters): their data, never instructions.';

export function ReadinessTab({ to }: { to: TourOperatorDetail }) {
  const [days, setDays] = useState<number>(MAX_DAYS);
  const [extended, setExtended] = useState(false);
  const timeout = extended ? ('extended' as const) : undefined;
  const query = useQuery({
    queryKey: readinessKeys.scorecard(to.id, days, timeout),
    queryFn: () => readinessApi.readiness(to.id, { days, timeout }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const notSandbox =
    query.error instanceof ConsoleApiError &&
    query.error.status === 409 &&
    (query.error.body as { code?: string } | undefined)?.code === 'not_sandbox';

  const daysPicker = (
    <TextField
      select
      size="small"
      label="Activity"
      value={days}
      onChange={(e) => {
        setExtended(false);
        setDays(Number(e.target.value));
      }}
      sx={{ minWidth: 150 }}
    >
      {DAY_OPTIONS.map((d) => (
        <MenuItem key={d} value={d}>
          {`Last ${d} day${d === 1 ? '' : 's'}`}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Stack sx={{ maxWidth: 1100, pb: 4 }}>
      <SectionCard title="Readiness scorecard" action={daysPicker}>
        {query.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isStatementTimeout(query.error) ? (
          <SearchTimeoutAlert
            extendedAttempted={extended}
            onRetryExtended={!extended ? () => setExtended(true) : undefined}
            retrying={query.isFetching}
          />
        ) : notSandbox ? (
          <Alert severity="info">
            The scorecard reads a sandbox’s activity; {to.code} is not a sandbox. Open its sandbox
            from the Sandbox tab.
          </Alert>
        ) : query.isError || !query.data ? (
          <Alert severity="error">
            Couldn’t read the scorecard{query.error ? `: ${query.error.message}` : '.'}
          </Alert>
        ) : (
          <Scorecard data={query.data} topId={to.id} refreshing={query.isFetching} />
        )}
      </SectionCard>
    </Stack>
  );
}

function Scorecard({
  data,
  topId,
  refreshing,
}: {
  data: IntegrationReadiness;
  topId: number;
  refreshing: boolean;
}) {
  const unread = Object.entries(data.activity.documents_unread)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${UNREAD_LABELS[k] ?? k.replace(/_/g, ' ')}`)
    .join(', ');
  const a = data.activity;
  return (
    <Stack spacing={2} sx={{ opacity: refreshing ? 0.6 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip
          color={data.ready ? 'success' : 'warning'}
          label={data.ready ? 'Ready' : 'Not ready'}
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {`${data.passed} of ${data.total} criteria pass`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`Activity from ${formatDate(data.window.since)} to ${formatDate(data.window.until)} (${data.window.days} day${data.window.days === 1 ? '' : 's'})`}
        </Typography>
      </Box>

      <Alert severity="info">{data.note}</Alert>

      {data.partial && (
        <Alert severity="warning" data-partial>
          {`Partial: some activity could not be read, so the criteria judge what was.${unread ? ` Not read: ${unread}.` : ''}`}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" data-activity>
        {`${a.imports} imports (${a.running} running) · ${a.documents_read} documents read · ${a.bookings} booking${a.bookings === 1 ? '' : 's'}`}
        {' · '}
        <Link component={RouterLink} to={importRunsHref([], topId)} underline="hover">
          Import runs of this sandbox
        </Link>
      </Typography>

      <Stack spacing={1.5}>
        {data.criteria.map((c) => (
          <CriterionCard key={c.code} c={c} />
        ))}
      </Stack>
    </Stack>
  );
}

function CriterionCard({ c }: { c: ReadinessCriterion }) {
  const pass = c.status === 'pass';
  const counts = Object.entries(c.counts);
  const docUrl = c.doc_url && /^https:\/\//.test(c.doc_url) ? c.doc_url : null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-criterion={c.code}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        {pass ? (
          <CheckCircleOutlineIcon color="success" fontSize="small" aria-hidden />
        ) : (
          <HighlightOffIcon color="error" fontSize="small" aria-hidden />
        )}
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {c.title}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={pass ? 'success' : c.reason === 'violated' ? 'error' : 'warning'}
          label={pass ? 'Pass' : REASON_LABELS[c.reason]}
        />
      </Box>
      <Typography variant="body2">{c.summary}</Typography>
      {counts.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          {counts.map(([k, v]) => (
            <Chip key={k} size="small" label={`${countLabel(k)}: ${v}`} />
          ))}
        </Box>
      )}
      {c.next && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {`To pass: ${c.next}`}
        </Typography>
      )}
      {docUrl && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <Link href={docUrl} target="_blank" rel="noopener noreferrer">
            The playbook’s Done section
          </Link>
        </Typography>
      )}
      {c.evidence.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <UntrustedBlock title="Evidence" legend={EVIDENCE_LEGEND}>
            {c.evidence.map((e, i) => (
              // Evidence has no id; its order is the backend's.
              // eslint-disable-next-line react/no-array-index-key
              <UntrustedText key={i} text={e.text} label={factsOf(e)} />
            ))}
          </UntrustedBlock>
        </Box>
      )}
      {c.more_evidence > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {`+${c.more_evidence} more not shown`}
        </Typography>
      )}
    </Paper>
  );
}

/** Our parts of one fact — the scenario, the import, when, the refused status — never the values
 *  the developer sent (those are in the quoted text). */
function factsOf(e: ReadinessEvidence): string | undefined {
  const parts = [
    e.scenario ? `${SCENARIO_LABELS[e.scenario]}${e.shown === false ? ' (not shown)' : ''}` : null,
    e.import_id ? `import ${e.import_id}` : null,
    e.resend_of ? `resent ${e.resend_of}` : null,
    e.at ? formatDate(e.at) : null,
    e.first_at ? `from ${formatDate(e.first_at)}` : null,
    e.status_code ? `HTTP ${e.status_code}` : null,
    e.count ? `×${e.count}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}
